import { Box } from '@mui/material';
import type { ReactElement } from 'react';

import { displayValue, MISSING_VALUE } from '../kit/displayValue';
import { isValue, type Maybe } from './geometry';
import type { SeriesToken } from './seriesPalette';
import { VISUALLY_HIDDEN } from './visuallyHidden';

/**
 * Barre de progression (maquette « Jauge » : piste de 6 px, remplissage arrondi).
 *
 * C'est un **`progressbar`** natif au sens ARIA : le nom (`label`), la valeur
 * (`aria-valuenow`) et sa lecture (`aria-valuetext`, « 62 % ») sont exposes
 * directement, sans table — une seule valeur n'en demande pas. L'appelant ecrit
 * a cote le pourcentage et l'etat en toutes lettres (la couleur ne dit jamais
 * seule si c'est bon ou grave).
 *
 * - `value` est **un pourcentage fourni** (0 a 100) : la barre ne le calcule pas.
 *   Hors de 0–100 elle se dessine pleine ou vide, mais `aria-valuetext` dit la
 *   vraie valeur plutot qu'une valeur rognee.
 * - `null` : **pas de barre** — une piste vide se lirait « 0 % » — mais un tiret
 *   et, pour les lecteurs d'ecran, « donnee absente ».
 */
export type ProgressTone = 'primary' | 'success' | 'warning' | 'critical' | 'neutral';

/** Encres, et non aplats : le remplissage doit tenir 3:1 sur la carte. */
const TONE_TOKEN: Record<ProgressTone, SeriesToken | 'criticalStrong'> = {
  primary: 'primary',
  success: 'successText',
  warning: 'accentYellowStrong',
  critical: 'criticalStrong',
  neutral: 'textMuted',
};

export interface ProgressBarProps {
  /** Nom accessible : ce qui progresse (« Disque utilise »). */
  readonly label: string;
  readonly value: Maybe;
  readonly tone?: ProgressTone;
  readonly width?: number | string;
}

export const ProgressBar = ({ label, value, tone = 'primary', width = '100%' }: ProgressBarProps): ReactElement => {
  if (!isValue(value)) {
    return (
      <Box component="span" sx={{ color: 'tokens.textMuted', fontSize: 13 }}>
        {MISSING_VALUE}
        <Box component="span" sx={{ ...VISUALLY_HIDDEN }}>
          {` ${label} : donnée absente`}
        </Box>
      </Box>
    );
  }

  const drawn = Math.min(100, Math.max(0, value));

  return (
    <Box
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={drawn}
      aria-valuetext={`${displayValue(value)} %`}
      sx={{ width, maxWidth: '100%', height: 6, borderRadius: '3px', bgcolor: 'tokens.neutralSoft', overflow: 'hidden' }}
    >
      <Box
        sx={{
          height: '100%',
          width: `${String(drawn)}%`,
          // Une valeur positive reste visible ; zero ne dessine rien.
          minWidth: drawn > 0 ? '2px' : 0,
          borderRadius: '3px',
          bgcolor: `tokens.${TONE_TOKEN[tone]}`,
        }}
      />
    </Box>
  );
};
